import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import axiosInstance from "../../../axiosInstance";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import {
  employmentTypeOptions,
  jobStatusUpdateOptions,
  workModeOptions,
} from "../../../helpers/jobPortalMeta";
import { getJobPortalAuthConfig } from "../../../helpers/jobPortalAuth";

const initialForm = {
  jobTitle: "",
  jobRole: "",
  category: "",
  department: "",
  employmentType: "full_time",
  workMode: "onsite",
  state: "",
  statename: "",
  city: "",
  zone: "",
  openings: 1,
  experienceMin: 0,
  experienceMax: 0,
  salaryMin: 0,
  salaryMax: 0,
  qualification: "",
  genderRequirement: 0,
  englishRequirement: 0,
  relocationAllowed: false,
  jobDescription: "",
  requirements: "",
  responsibilities: "",
  benefits: "",
  interviewDetails: "",
  companyName: "",
  contactPerson: "",
  phone: "",
  email: "",
  address: "",
  companyProfile: "",
  companySize: "",
  jobStatus: "active",
  adminApprovalStatus: "approved",
  skillsRequired: [],
};

const RecruiterJobForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [masterDataLoading, setMasterDataLoading] = useState(true);
  const [formData, setFormData] = useState(initialForm);
  const [zones, setZones] = useState([]);
  const [skills, setSkills] = useState([]);
  const [functionalAreas, setFunctionalAreas] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [subscription, setSubscription] = useState(null);

  const selectedStateId = useMemo(
    () => zones.find((zone) => zone.name === formData.statename)?._id || "",
    [zones, formData.statename]
  );

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (type === "checkbox" && name === "skillsRequired") {
      setFormData((prev) => ({
        ...prev,
        skillsRequired: checked
          ? [...prev.skillsRequired, value]
          : prev.skillsRequired.filter((item) => item !== value),
      }));
      return;
    }

    if (name === "state") {
      const selectedZone = zones.find((zone) => zone._id === value);
      setFilteredCities(selectedZone?.cities || []);
      setFormData((prev) => ({
        ...prev,
        state: value,
        statename: selectedZone?.name || "",
        city: "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const fetchMasterData = async () => {
    try {
      setMasterDataLoading(true);
      const [
        skillsResponse,
        areasResponse,
        departmentsResponse,
        industriesResponse,
        zonesResponse,
      ] = await Promise.all([
        axiosInstance.get("/get-all-skill"),
        axiosInstance.get("/get-all-specialization"),
        axiosInstance.get("/get-all-department"),
        axiosInstance.get("/get-all-business"),
        axiosInstance.get("/get-all-zones"),
      ]);

      setSkills(skillsResponse.data?.Skill || []);
      setFunctionalAreas(areasResponse.data?.Specialization || []);
      setDepartments(departmentsResponse.data?.Department || []);
      setIndustries(industriesResponse.data?.Business || []);
      setZones(zonesResponse.data?.Zones || []);
    } catch (error) {
      console.error(error);
      toast.error("Unable to load job form data");
    } finally {
      setMasterDataLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const authConfig = await getJobPortalAuthConfig();
      const response = await axiosInstance.get("/job-portal/subscription/me", authConfig);
      setSubscription(response.data?.data?.subscription || null);
    } catch (error) {
      console.error(error);
    }
  };

  const prefillRecruiterProfile = async () => {
    if (isEditMode) {
      return;
    }

    try {
      const authConfig = await getJobPortalAuthConfig();
      const response = await axiosInstance.post("/auth-user", {
        id: authConfig.headers["x-auth-user-id"],
      });
      const existingUser = response.data?.existingUser;

      if (!existingUser) {
        return;
      }

      setFormData((prev) => ({
        ...prev,
        companyName: prev.companyName || existingUser.username || "",
        contactPerson: prev.contactPerson || existingUser.username || "",
        phone: prev.phone || existingUser.phone || "",
        email: prev.email || existingUser.email || "",
        address: prev.address || existingUser.address || "",
        companyProfile: prev.companyProfile || existingUser.about || "",
        companySize:
          prev.companySize || String(existingUser.numberOfBeds || ""),
        statename: prev.statename || existingUser.statename || "",
        city: prev.city || existingUser.city || "",
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const fetchJobDetails = async () => {
    if (!isEditMode) {
      return;
    }

    try {
      setLoading(true);
      const authConfig = await getJobPortalAuthConfig();
      const response = await axiosInstance.get(`/job-portal/jobs/${id}`, authConfig);
      const job = response.data?.data?.job;

      if (!job) {
        throw new Error("Job not found");
      }

      const matchingZone = zones.find(
        (zone) => zone.name === (job.location?.state || "")
      );

      setFilteredCities(matchingZone?.cities || []);
      setFormData((prev) => ({
        ...prev,
        jobTitle: job.title || "",
        jobRole: job.roleTitle || "",
        category: job.category || "",
        department: job.department || "",
        employmentType: job.employmentType || "full_time",
        workMode: job.workMode || "onsite",
        state: matchingZone?._id || "",
        statename: job.location?.state || "",
        city: job.location?.city || "",
        zone: job.location?.zone || "",
        openings: job.openings || 1,
        experienceMin: job.experience?.minimum || 0,
        experienceMax: job.experience?.maximum || 0,
        salaryMin: job.salary?.minimum || 0,
        salaryMax: job.salary?.maximum || 0,
        qualification: job.qualification || "",
        genderRequirement: job.candidatePreferences?.gender ?? 0,
        englishRequirement: job.candidatePreferences?.englishRequirement ?? 0,
        relocationAllowed: Boolean(job.candidatePreferences?.relocation),
        jobDescription: job.description || "",
        requirements: job.requirements || "",
        responsibilities: job.responsibilities || "",
        benefits: job.benefits || "",
        interviewDetails: job.interviewDetails || "",
        companyName: job.company?.name || "",
        contactPerson: job.company?.contactPerson || "",
        phone: job.company?.phone || "",
        email: job.company?.email || "",
        address: job.company?.address || "",
        companyProfile: job.company?.profile || "",
        companySize: job.company?.size || "",
        jobStatus: job.jobStatus || "active",
        adminApprovalStatus: job.adminApprovalStatus || "approved",
        skillsRequired: job.skills || [],
      }));
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Unable to load job details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterData();
    prefillRecruiterProfile();
    fetchSubscription();
  }, []);

  useEffect(() => {
    if (!masterDataLoading) {
      fetchJobDetails();
    }
  }, [masterDataLoading, id, zones.length]);

  const submitForm = async () => {
    if (!isEditMode && subscription?.status !== "active") {
      toast.error("An active recruiter plan is required to post jobs");
      navigate("/recruiter/plans");
      return;
    }

    try {
      setLoading(true);
      const authConfig = await getJobPortalAuthConfig();
      const payload = {
        jobTitle: formData.jobTitle,
        jobRole: formData.jobRole,
        category: formData.category,
        department: formData.department,
        employmentType: formData.employmentType,
        workMode: formData.workMode,
        statename: formData.statename,
        city: formData.city,
        zone: formData.zone,
        openings: Number(formData.openings),
        experienceMin: Number(formData.experienceMin),
        experienceMax: Number(formData.experienceMax),
        salaryMin: Number(formData.salaryMin),
        salaryMax: Number(formData.salaryMax),
        qualification: formData.qualification,
        genderRequirement: Number(formData.genderRequirement),
        englishRequirement: Number(formData.englishRequirement),
        relocationAllowed: formData.relocationAllowed,
        jobDescription: formData.jobDescription,
        requirements: formData.requirements,
        responsibilities: formData.responsibilities,
        benefits: formData.benefits,
        interviewDetails: formData.interviewDetails,
        companyName: formData.companyName,
        contactPerson: formData.contactPerson,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        companyProfile: formData.companyProfile,
        companySize: formData.companySize,
        skillsRequired: formData.skillsRequired,
        jobStatus: formData.jobStatus,
        adminApprovalStatus: formData.adminApprovalStatus,
      };

      if (isEditMode) {
        await axiosInstance.put(`/job-portal/jobs/${id}`, payload, authConfig);
        toast.success("Job updated successfully");
      } else {
        await axiosInstance.post("/job-portal/jobs", payload, authConfig);
        toast.success("Job posted successfully");
      }

      navigate("/recruiter/jobs");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Unable to save job");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="py-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h1 className="h4 mb-1">{isEditMode ? "Edit Job" : "Post a New Job"}</h1>
            <p className="text-muted mb-0">
              Create clear job posts and keep all recruiter-owned fields in one place.
            </p>
          </div>
          <Link to="/recruiter/jobs" className="btn btn-outline-primary">
            Back to Jobs
          </Link>
        </div>

        {!isEditMode && subscription?.status !== "active" && (
          <div className="alert alert-warning border-0 shadow-sm">
            An active recruiter plan is required to post a new job.
            <Link to="/recruiter/plans" className="ms-2 alert-link">
              Buy Plan
            </Link>
          </div>
        )}

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            {masterDataLoading ? (
              <div>Loading form...</div>
            ) : (
              <div className="row g-4">
                <div className="col-lg-8">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Job Title</label>
                      <input className="form-control" name="jobTitle" value={formData.jobTitle} onChange={handleChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Job Role</label>
                      <select className="form-select" name="jobRole" value={formData.jobRole} onChange={handleChange}>
                        <option value="">Select role</option>
                        {functionalAreas.map((item) => (
                          <option key={item._id} value={item.name}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Category / Industry</label>
                      <select className="form-select" name="category" value={formData.category} onChange={handleChange}>
                        <option value="">Select category</option>
                        {industries.map((item) => (
                          <option key={item._id} value={item.name}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Department / Team</label>
                      <select className="form-select" name="department" value={formData.department} onChange={handleChange}>
                        <option value="">Select team</option>
                        {departments.map((item) => (
                          <option key={item._id} value={item.name}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Employment Type</label>
                      <select className="form-select" name="employmentType" value={formData.employmentType} onChange={handleChange}>
                        {employmentTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Work Mode</label>
                      <select className="form-select" name="workMode" value={formData.workMode} onChange={handleChange}>
                        {workModeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Job Status</label>
                      <select className="form-select" name="jobStatus" value={formData.jobStatus} onChange={handleChange}>
                        {jobStatusUpdateOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">State</label>
                      <select className="form-select" name="state" value={selectedStateId || formData.state} onChange={handleChange}>
                        <option value="">Select state</option>
                        {zones.map((zone) => (
                          <option key={zone._id} value={zone._id}>
                            {zone.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">City</label>
                      <select className="form-select" name="city" value={formData.city} onChange={handleChange}>
                        <option value="">Select city</option>
                        {filteredCities.map((city) => (
                          <option key={city} value={city}>
                            {city}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Zone</label>
                      <input className="form-control" name="zone" value={formData.zone} onChange={handleChange} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Openings</label>
                      <input className="form-control" type="number" name="openings" value={formData.openings} onChange={handleChange} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Min Experience</label>
                      <input className="form-control" type="number" name="experienceMin" value={formData.experienceMin} onChange={handleChange} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Max Experience</label>
                      <input className="form-control" type="number" name="experienceMax" value={formData.experienceMax} onChange={handleChange} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Qualification</label>
                      <input className="form-control" name="qualification" value={formData.qualification} onChange={handleChange} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Min Salary</label>
                      <input className="form-control" type="number" name="salaryMin" value={formData.salaryMin} onChange={handleChange} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Max Salary</label>
                      <input className="form-control" type="number" name="salaryMax" value={formData.salaryMax} onChange={handleChange} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Gender Requirement</label>
                      <select className="form-select" name="genderRequirement" value={formData.genderRequirement} onChange={handleChange}>
                        <option value={0}>Optional / Any</option>
                        <option value={2}>Male</option>
                        <option value={3}>Female</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">English Requirement</label>
                      <select className="form-select" name="englishRequirement" value={formData.englishRequirement} onChange={handleChange}>
                        <option value={0}>Optional</option>
                        <option value={1}>Basic</option>
                        <option value={2}>Conversational</option>
                        <option value={3}>Good</option>
                        <option value={4}>Fluent</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label d-block">Skills Required</label>
                      {skills.map((skill) => (
                        <label key={skill._id} className="btn btn-sm btn-outline-primary me-2 mb-2">
                          <input
                            className="form-check-input me-2"
                            type="checkbox"
                            name="skillsRequired"
                            value={skill.name}
                            checked={formData.skillsRequired.includes(skill.name)}
                            onChange={handleChange}
                          />
                          {skill.name}
                        </label>
                      ))}
                    </div>
                    <div className="col-12">
                      <label className="form-label">Job Description</label>
                      <textarea className="form-control" rows={4} name="jobDescription" value={formData.jobDescription} onChange={handleChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Requirements</label>
                      <textarea className="form-control" rows={4} name="requirements" value={formData.requirements} onChange={handleChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Responsibilities</label>
                      <textarea className="form-control" rows={4} name="responsibilities" value={formData.responsibilities} onChange={handleChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Benefits</label>
                      <textarea className="form-control" rows={4} name="benefits" value={formData.benefits} onChange={handleChange} />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Interview Details</label>
                      <textarea className="form-control" rows={3} name="interviewDetails" value={formData.interviewDetails} onChange={handleChange} />
                    </div>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div className="border rounded p-3">
                    <h2 className="h6 mb-3">Company Details</h2>
                    <div className="mb-3">
                      <label className="form-label">Organization Name</label>
                      <input className="form-control" name="companyName" value={formData.companyName} onChange={handleChange} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Contact Person</label>
                      <input className="form-control" name="contactPerson" value={formData.contactPerson} onChange={handleChange} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Phone</label>
                      <input className="form-control" name="phone" value={formData.phone} onChange={handleChange} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Email</label>
                      <input className="form-control" name="email" value={formData.email} onChange={handleChange} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Company Size</label>
                      <input className="form-control" name="companySize" value={formData.companySize} onChange={handleChange} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Company Profile</label>
                      <textarea className="form-control" rows={3} name="companyProfile" value={formData.companyProfile} onChange={handleChange} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Address</label>
                      <textarea className="form-control" rows={3} name="address" value={formData.address} onChange={handleChange} />
                    </div>
                    <div className="form-check mb-3">
                      <input className="form-check-input" type="checkbox" id="relocationAllowed" name="relocationAllowed" checked={formData.relocationAllowed} onChange={handleChange} />
                      <label className="form-check-label" htmlFor="relocationAllowed">
                        Allow relocation applicants
                      </label>
                    </div>
                    <button
                      className="btn btn-primary w-100"
                      onClick={submitForm}
                      disabled={loading || (!isEditMode && subscription?.status !== "active")}
                    >
                      {loading ? "Saving..." : isEditMode ? "Update Job" : "Create Job"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default RecruiterJobForm;
